import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { LanguageProvider } from "@/i18n/i18n";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { HelmetProvider } from "react-helmet-async";
import PublicLayout from "@/components/PublicLayout";
import DashboardLayout from "@/components/DashboardLayout";

import Home from "@/pages/public/Home";
import { Mission, HowItWorks, WhoWeServe, ZeroRot, FarmerProtection, Institutional, Circular, Soil, Governance } from "@/pages/public/PublicPages";
import Investors from "@/pages/public/Investors";
import Contact from "@/pages/public/Contact";
import Legal from "@/pages/public/Legal";
import PublicRescue from "@/pages/public/PublicRescue";
import Trace from "@/pages/public/Trace";
import StrawberryBrief from "@/pages/public/StrawberryBrief";
import Founder from "@/pages/public/Founder";
import PolskaKwitnie from "@/pages/public/PolskaKwitnie";
import HoneyTrace from "@/pages/public/HoneyTrace";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";

import DashboardHome from "@/pages/app/DashboardHome";
import CropsPage from "@/pages/app/CropsPage";
import DemandPage from "@/pages/app/DemandPage";
import ProcessingPage from "@/pages/app/ProcessingPage";
import RescuePage from "@/pages/app/RescuePage";
import MatchingPage from "@/pages/app/MatchingPage";
import SettlementsPage from "@/pages/app/SettlementsPage";
import { LogisticsPage, RecoveryPage, DisputesPage } from "@/pages/app/OpsPages";
import ProposalsPage from "@/pages/app/ProposalsPage";
import InvestorRoomPage from "@/pages/app/InvestorRoomPage";
import GovernancePage from "@/pages/app/GovernancePage";
import { AdminPage, LeadsPage, AuditPage, UsersPage } from "@/pages/app/AdminPages";
import ImpactPage from "@/pages/app/ImpactPage";
import { ApprovalsPage, SoilPage } from "@/pages/app/AdvancedPages";
import { PollinationPage, TreatmentAlertsPage, HabitatPage, HoneyPage } from "@/pages/app/PollinatorPages";

function Loader() {
  return <div className="grid min-h-screen place-items-center bg-offwhite text-forest">Ładowanie…</div>;
}

function Protected() {
  const { user, ready } = useAuth();
  if (!ready) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  return <DashboardLayout />;
}

function PublicWrap() {
  return <PublicLayout><Outlet /></PublicLayout>;
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors />
            <Routes>
              <Route element={<PublicWrap />}>
                <Route path="/" element={<Home />} />
                <Route path="/mission" element={<Mission />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/who-we-serve" element={<WhoWeServe />} />
                <Route path="/zero-rot" element={<ZeroRot />} />
                <Route path="/farmer-protection" element={<FarmerProtection />} />
                <Route path="/institutional-food" element={<Institutional />} />
                <Route path="/circular-economy" element={<Circular />} />
                <Route path="/soil-planning" element={<Soil />} />
                <Route path="/governance" element={<Governance />} />
                <Route path="/investors" element={<Investors />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/legal/:doc" element={<Legal />} />
                <Route path="/rescue-preview" element={<PublicRescue />} />
                <Route path="/trace/:traceId" element={<Trace />} />
                <Route path="/strawberry-brief" element={<StrawberryBrief />} />
                <Route path="/polska-kwitnie" element={<PolskaKwitnie />} />
                <Route path="/konrad-kwiatkowski" element={<Founder />} />
                <Route path="/honey/:batchId" element={<HoneyTrace />} />
              </Route>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/app" element={<Protected />}>
                <Route index element={<DashboardHome />} />
                <Route path="crops" element={<CropsPage />} />
                <Route path="demand" element={<DemandPage />} />
                <Route path="processing" element={<ProcessingPage />} />
                <Route path="rescue" element={<RescuePage />} />
                <Route path="matching" element={<MatchingPage />} />
                <Route path="settlements" element={<SettlementsPage />} />
                <Route path="logistics" element={<LogisticsPage />} />
                <Route path="recovery" element={<RecoveryPage />} />
                <Route path="disputes" element={<DisputesPage />} />
                <Route path="proposals" element={<ProposalsPage />} />
                <Route path="investor" element={<InvestorRoomPage />} />
                <Route path="governance" element={<GovernancePage />} />
                <Route path="impact" element={<ImpactPage />} />
                <Route path="leads" element={<LeadsPage />} />
                <Route path="users" element={<UsersPage />} />
                <Route path="audit" element={<AuditPage />} />
                <Route path="settings" element={<AdminPage />} />
                <Route path="approvals" element={<ApprovalsPage />} />
                <Route path="soil" element={<SoilPage />} />
                <Route path="investor-brief" element={<StrawberryBrief />} />
                <Route path="pollination" element={<PollinationPage />} />
                <Route path="treatment-alerts" element={<TreatmentAlertsPage />} />
                <Route path="habitat" element={<HabitatPage />} />
                <Route path="honey" element={<HoneyPage />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
